import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { getElevateUser, getSelectedUnit } from '../../../storage/Storage';
import { searchTask } from '../../../constant/Constant';
import { HitApi } from '../../../Api/ApiHit';
import { useDispatch } from 'react-redux';
import { setPagination } from '../../../redux/Actions/TableAction';
import { monthWeeksOptions, weekDaysOptions } from '../../../constant/Options';

const MonthlyTaskCalendar = ({ selectedUserId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const dispatch = useDispatch();

  // Use selectedUserId if provided (for admin view), otherwise use logged-in user
  const userId = selectedUserId || getElevateUser()?._id;

  const getMonthlyTask = () => {
    setLoading(true);
    var json = {
      page: 1,
      limit: 100, // Get more tasks for calendar view
      search: {
        assignedTo: { $in: [userId] },
        unitIds: getSelectedUnit()?._id,
        taskFrequency: "monthly"
      },
    };

    HitApi(json, searchTask).then((res) => {
      console.log("API Response:", res);
      const paginationData = {
        total: res?.data?.totalDocs || 0,
        page: 1,
        limit: 100,
      };

      dispatch(setPagination(paginationData));
      if (res.statusCode === 200 && res.data?.docs) {
        const tasksData = res.data.docs || [];
        setTasks(tasksData);
      } else {
        setError('Failed to load tasks');
      }
      setLoading(false);
    }).catch((err) => {
      console.error("API Error:", err);
      setError('Error loading tasks');
      setLoading(false);
    });
  };

  useEffect(() => {
    getMonthlyTask();
  }, [selectedUserId]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helper functions to work with your options
  const getDayLabel = (dayValue) => {
    const option = weekDaysOptions.find(opt => opt.value === parseInt(dayValue));
    return option ? option.label : '';
  };

  const getWeekLabel = (weekValue) => {
    const option = monthWeeksOptions.find(opt => opt.value === parseInt(weekValue));
    return option ? option.label : '';
  };

  // Convert your day numbering (1-7, Mon-Sun) to JavaScript's (0-6, Sun-Sat)
  const convertToJSDay = (yourDayNumber) => {
    const dayNum = parseInt(yourDayNumber);
    return dayNum === 7 ? 0 : dayNum; // Sunday (7) becomes 0, others stay same
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getWeekOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfMonth = date.getDate();
    return Math.ceil((dayOfMonth + firstDay.getDay()) / 7);
  };

  const isLastWeekOfMonth = (date) => {
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const weekOfMonth = getWeekOfMonth(date);
    const lastWeekOfMonth = getWeekOfMonth(lastDay);
    return weekOfMonth === lastWeekOfMonth;
  };

  const getTasksForDate = (date) => {
    const jsDayOfWeek = date.getDay(); // 0-6 (Sun-Sat)
    const weekOfMonth = getWeekOfMonth(date);
    const isLastWeek = isLastWeekOfMonth(date);

    return tasks.filter(task => {
      const taskDays = task.weekDaysForMonthly?.map(day => parseInt(day)) || [];
      const taskWeeks = task.monthWeeks?.map(week => parseInt(week)) || [];

      // Convert your day numbers to JS day numbers for comparison
      const jsTaskDays = taskDays.map(convertToJSDay);

      // Check if this day matches the task's schedule
      const matchesDay = taskDays.length === 0 || jsTaskDays.includes(jsDayOfWeek);
      const matchesWeek = taskWeeks.length === 0 ||
        taskWeeks.includes(weekOfMonth) ||
        (taskWeeks.includes(5) && isLastWeek);

      return matchesDay && matchesWeek;
    });
  };

  const formatTime = (timeString) => {
    if (!timeString || !timeString.includes('-')) return timeString;
    const [startTime] = timeString.split('-');
    const [hours, minutes] = startTime.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'bg-red-100 border-red-300 text-red-800';
      case 'medium': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'low': return 'bg-green-100 border-green-300 text-green-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <XCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-32 border border-gray-100"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayTasks = getTasksForDate(date);
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

      days.push(
        <div
          key={day}
          className={`h-32 border border-gray-100 p-2 cursor-pointer transition-all duration-200 hover:bg-blue-50 ${isToday ? 'bg-blue-50 border-blue-300' : ''
            } ${isSelected ? 'bg-indigo-100 border-indigo-400 ring-2 ring-indigo-200' : ''}`}
          onClick={() => setSelectedDate(date)}
        >
          <div className={`text-sm font-semibold mb-2 ${isToday ? 'text-blue-600' : 'text-gray-700'
            }`}>
            {day}
          </div>

          <div className="space-y-1">
            {dayTasks.slice(0, 2).map((task, index) => (
              <div
                key={`${task._id}-${index}`}
                className={`text-xs p-1 rounded border ${getPriorityColor(task.priority)} truncate`}
                title={`${task.taskName} - ${task.description}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium truncate">{task.taskName}</span>
                  {getStatusIcon(task.taskIntervals?.[0]?.status)}
                </div>
                {task.taskIntervals?.[0]?.interval && (
                  <div className="text-xs opacity-75 mt-1">
                    {formatTime(task.taskIntervals[0].interval)}
                  </div>
                )}
              </div>
            ))}
            {dayTasks.length > 2 && (
              <div className="text-xs text-gray-500 font-medium bg-gray-100 p-1 rounded">
                +{dayTasks.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="mx-auto border">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Clock className="w-12 h-12 mx-auto mb-4 text-blue-500 animate-spin" />
          <p className="text-lg text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  // Show no data found when no tasks
  if (!loading && tasks.length === 0) {
    return (
      <div className="mx-auto border">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2 flex-wrap">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />
                  Monthly Task Calendar
                </h1>
                <p className="text-blue-100 mt-1 text-sm sm:text-base">
                  Manage and track your monthly recurring tasks
                </p>
              </div>
            </div>
          </div>

          <div className="p-8 text-center">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Found</h3>
            <p className="text-gray-500 mb-4">
              You don't have any monthly recurring tasks assigned yet.
            </p>
            <p className="text-sm text-gray-400">
              Contact your administrator to assign monthly tasks or check your task filters.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="mx-auto border">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Tasks</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => getMonthlyTask()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className=" mx-auto border">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2 flex-wrap">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />
                Monthly Task Calendar
              </h1>
              <p className="text-blue-100 mt-1 text-sm sm:text-base">
                Manage and track your monthly recurring tasks
              </p>
            </div>
          </div>

        </div>
        <div className="p-4 border-b border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              {/* Previous Button */}
              <button
                onClick={() => navigateMonth(-1)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm sm:text-base"
              >
                {/* Mobile: only arrow */}
                <span className="block sm:hidden">←</span>
                {/* Desktop: full text */}
                <span className="hidden sm:block">← Previous</span>
              </button>

              {/* Month / Year */}
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 text-center">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>

              {/* Next Button */}
              <button
                onClick={() => navigateMonth(1)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm sm:text-base"
              >
                {/* Mobile: only arrow */}
                <span className="block sm:hidden">→</span>
                {/* Desktop: full text */}
                <span className="hidden sm:block">Next →</span>
              </button>
            </div>
          </div>

        </div>


      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {dayNames.map(day => (
            <div key={day} className="p-4 text-center border-r border-gray-200 last:border-r-0">
              <span className="text-sm font-semibold text-gray-700">{day}</span>
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {renderCalendarDays()}
        </div>
      </div>

      {/* Task Details Panel */}
      {selectedDate && (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 flex-wrap">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            Tasks for{" "}
            {selectedDate.toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </h3>

          <div className="space-y-3 sm:space-y-4">
            {getTasksForDate(selectedDate).map((task, index) => (
              <div
                key={`${task._id}-detail-${index}`}
                className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
              >
                {/* Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 sm:mb-3 gap-2">
                  <div>
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2 text-sm sm:text-base">
                      {task.priority === "high" && (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      )}
                      {task.taskName}
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm mt-1">
                      {task.description}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-[10px] sm:text-xs font-medium self-start sm:self-auto ${getPriorityColor(
                      task.priority
                    )}`}
                  >
                    {task.priority?.toUpperCase()}
                  </span>
                </div>

                {/* Schedule Info */}
                <div className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                  <strong>Schedule:</strong>
                  {task.weekDaysForMonthly?.length > 0 && (
                    <span className="ml-1">
                      {task.weekDaysForMonthly
                        .map((day) => getDayLabel(day))
                        .join(", ")}
                    </span>
                  )}
                  {task.monthWeeks?.length > 0 && (
                    <span className="ml-1">
                      (
                      {task.monthWeeks
                        .map((week) => getWeekLabel(week))
                        .join(", ")}
                      )
                    </span>
                  )}
                </div>

                {/* Time Intervals */}
                {task.taskIntervals?.length > 0 && (
                  <div className="mb-2 sm:mb-3">
                    <strong className="text-xs sm:text-sm text-gray-700">
                      Time Slots:
                    </strong>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {task.taskIntervals.map((interval, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-1 sm:gap-2 bg-gray-50 px-2 py-1 rounded text-xs sm:text-sm"
                        >
                          <Clock className="w-3 h-3 text-gray-500" />
                          <span className="font-mono">{interval.interval}</span>
                          {getStatusIcon(interval.status)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {getTasksForDate(selectedDate).length === 0 && (
              <div className="text-center py-6 sm:py-8 text-gray-500">
                <Calendar className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm sm:text-base">
                  No tasks scheduled for this date
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{tasks.length}</div>
          <div className="text-sm text-gray-600">Total Tasks</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-2xl font-bold text-red-600">
            {tasks.filter(t => t.priority === 'high').length}
          </div>
          <div className="text-sm text-gray-600">High Priority</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {tasks.reduce((acc, task) => acc + (task.assignedTo?.length || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Assigned Users</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {tasks.reduce((acc, task) => acc + (task.taskIntervals?.length || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Time Slots</div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyTaskCalendar;